import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const VERSION_CHECK_INTERVAL = 60_000; // 60 seconds

// Build id of the bundle this tab is running. Undefined outside a Vite
// build (e.g. under Jest), in which case the first fetch sets the baseline.
const getBundleVersion = () =>
  typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;

/**
 * Polls /version.json to detect new deployments.
 * Returns `isUpdateAvailable` so the UI can show a popup
 * letting the user reload when they choose.
 *
 * No forced reload — avoids thundering-herd on the backend
 * when many users' tabs detect a new deploy within the same
 * ~60s window.
 *
 * Checks happen:
 * 1. On every route navigation
 * 2. Every 60 seconds via interval
 * 3. When the browser tab regains focus (visibilitychange)
 */
export function useVersionCheck() {
  const location = useLocation();
  // Seeded from the bundle, not from the first fetch: a tab opened from a
  // stale cached index.html would otherwise adopt the new deploy's id as
  // its own and never prompt.
  const currentVersion = useRef<string | null>(getBundleVersion());
  const isChecking = useRef(false);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  const checkVersion = async () => {
    if (isChecking.current || isUpdateAvailable) return;
    isChecking.current = true;

    try {
      const res = await fetch(`/version.json?_t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;

      const data = await res.json();
      const serverVersion = data?.version;
      if (!serverVersion) return;

      if (currentVersion.current === null) {
        // No bundle id (non-Vite run) — store the first version seen
        currentVersion.current = serverVersion;
        return;
      }

      if (currentVersion.current !== serverVersion) {
        console.info(
          `[VersionCheck] New deployment detected: ${currentVersion.current} → ${serverVersion}`,
        );
        setIsUpdateAvailable(true);
      }
    } catch {
      // Network error — skip silently
    } finally {
      isChecking.current = false;
    }
  };

  // Check on route change
  useEffect(() => {
    checkVersion();
  }, [location.pathname]);

  // Periodic check + visibility change
  useEffect(() => {
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const reload = () => window.location.reload();

  return { isUpdateAvailable, reload };
}
